import * as React from 'react';
import axios, { CancelTokenSource, CancelTokenStatic } from 'axios';
import themePrimary from '../../styles/themePrimary';
import clsx from 'clsx';
import { useHistory } from 'react-router';
import { ErrorCode } from '../../models/common/ErrorCode';
import { FormControl, IconButton, makeStyles, Select, Typography, useMediaQuery } from '@material-ui/core';
import { UserStateContext } from '../../providers/UserStateContextProvider';
import { CheckOutState, CheckOutStateContext } from '../../providers/CheckOutStateContextProvider';
import { ElementDimensions } from '../../styles/ElementDimensions';
import ErrorDialog, { ErrorDialogProps } from '../common/ErrorDialog';
import { String_PayCheck_CardExpirationDateFormatError } from '../../utilities/StringHelper';
import { CheckState, CheckStateContext } from '../../providers/CheckStateContextProvider';
import PaymentTerms from './PaymentTerms';
import GooglePayButton from '@google-pay/button-react';
import VisibilityIcon from '@material-ui/icons/Visibility';
import VisibilityOffIcon from '@material-ui/icons/VisibilityOff';
import { ApplePaySessionRequest } from '../../models/payment/ApplePaySessionRequest';
import { ApplePaySessionResponse } from '../../models/payment/ApplePaySessionResponse';
import { PaymentDetail } from '../../models/payment/PaymentDetail';
import { MakePaymentRequest } from '../../models/payment/MakePaymentRequest';
import { PaymentDigitalWalletType } from '../../models/payment/PaymentDigitalWalletType';
import { String_Check_CheckPaid, String_PayCheck_ErrorCreditCard, String_PayCheck_ErrorPaymentFailed, String_PayCheck_PaymentExceedsAmountDue, String_System_ConnectionError, String_System_GeneralError } from '../../utilities/StringHelper';
import TextField, { TextFieldChangeHandler, TextFieldLabelPosition, TextFieldProps } from '../common/TextField';
import { MakePaymentResponse } from '../../models/payment/MakePaymentResponse';
import { SplitType } from '../../models/common/SplitType';
import { PaymentMethodsAccepted } from '../../models/payment/PaymentMethodsAccepted';

const useStyles = makeStyles({
    creditButton: {
        backgroundColor: '#000000 !important',
        color: themePrimary.palette.primary.contrastText,    
        boxShadow: 'none',
        fontSize: '1.5em',
        width: '300px',
        height: '46px',
        lineHeight: '46px',
        borderRadius: '5px',
        marginTop: '20px',
        marginLeft: 'auto',
        marginRight: 'auto',
        fontFamily: 'LeagueSpartan-Regular !important',
        fontWeight: 'bold',
    },
    errorMessageContainer: {
        marginLeft: 'auto',
        marginRight: 'auto',
        textAlign: 'center',
        height: '2em'
    },
    errorMessage: {
        fontSize: '1rem',
        color: themePrimary.palette.error.main,
        fontFamily: 'LeagueSpartan-Regular !important',
    },
    expDateDivider: {
        marginLeft: ElementDimensions.margin10 + 'px',
        marginRight: ElementDimensions.margin10 + 'px',
    },
    expFields: {
        display: 'flex',
        marginRight: ElementDimensions.margin20 + 'px',
    },
    expFieldsInner: {
        display: 'flex',
        
    },
    expMonth: {
        width: '80px',
    },
    expYear: {
        width: '100px',
    },
    expDateCvvContainer: {
        display: 'flex',
    },
    fieldContainer: {
        marginLeft: ElementDimensions.margin20 + 'px',
    },
    fieldLabel: {
        textAlign: 'left',
    },
    googlePayButton: {
        width: '300px',
        height: '46px',
        marginTop: '20px !important',
        marginBottom: '0px !important',
    },
    maskButton: {
        height: '45px',        
    },
    paymentTerms: {
        marginLeft: ElementDimensions.margin20 + 'px',
        marginRight: ElementDimensions.margin20 + 'px',
        marginBottom: ElementDimensions.margin20 + 'px',
    },
    root: {
        textAlign: 'center',
        height: 'calc(100vh - ' + (73 + ElementDimensions.appBarHeight + ElementDimensions.stepperHeight + ElementDimensions.footerBarHeightPaymentScreen) + 'px)',
    },
    rootLandscape: {
        textAlign: 'center',
        height: '150%',
    },
    spreedlyFields: {
        height: '60px',
    },
    spreedlyMaskedButton: {
        height: '60px',
        marginLeft: ElementDimensions.margin10 + ' px',
    },
    spreedlyNumberContainer: {
        display: 'flex',
    },
});

export type SpreedlyGooglePaySuccessHandler = (paymentRequest: any) => any;
export type SpreedlySuccessHandler = (token: string) => any;
export type SpreedlyReadyHandler = (ready: boolean) => any;
export type SpreedlyErrorHandler = () => any;

export interface SpreedlyPaymentProps {
    paymentMethodAllowed: PaymentMethodsAccepted | undefined,
    total: number,
    submitPayment: boolean,
    readyHandler: SpreedlyReadyHandler,
    successHandler: SpreedlySuccessHandler,
    errorHandler: SpreedlyErrorHandler,
    googlePaySuccessHandler: SpreedlyGooglePaySuccessHandler
}

export default function SpreedlyPayment(props: SpreedlyPaymentProps) {
    const { paymentMethodAllowed, total, submitPayment, readyHandler, successHandler, errorHandler, googlePaySuccessHandler } = props;
    const isPortrait = true;
    const applePayEl = React.useRef(null);
    let history = useHistory();
    const [acceptedPaymentTypes, setAcceptedPaymentTypes] = React.useState('');
    const [isError, setIsError] = React.useState<boolean>(false);
    const [expMonth, setExpMonth] = React.useState<string>('MM');
    const [expYear, setExpYear] = React.useState<string>('YYYY');
    const [isLoaded, setIsLoaded] = React.useState<boolean>(false);
    const [isCreditCardSelected, setIsCreditCardSelected] = React.useState<boolean>(false);
    const [isMasked, setIsMasked] = React.useState<boolean>(true);
    const [ccNumberReady, setCcNumberReady] = React.useState<boolean>(false);
    const [cvvReady, setCvvReady] = React.useState<boolean>(false);
    const [applePayLoaded, setApplePayLoaded] = React.useState<boolean>(false);
    const [errorDialogOpen, setErrorDialogOpen] = React.useState<boolean>(false);
    const [errorMessage, setErrorMessage] = React.useState<string>('');
    const [errorDialogTryAgain, setErrorDialogTryAgain] = React.useState<boolean>(true);
    const [checkPaidError, setCheckPaidError] = React.useState<boolean>(false);
    const [paymentExceedsAmountDueError, setPaymentExceedsAmountDueError] = React.useState<boolean>(false);
    const [paymentError, setPaymentError] = React.useState<boolean>(false);
    const [totalChangedError, setTotalChangedError] = React.useState<boolean>(false);
    const userState = React.useContext(UserStateContext);
    const checkOutState = React.useContext(CheckOutStateContext);
    const checkState = React.useContext(CheckStateContext);
    const classes = useStyles({ primary_main_color: checkOutState.state.skin.primary_main_color, 
        primary_light_color: checkOutState.state.skin.primary_light_color, primary_dark_color: checkOutState.state.skin.primary_dark_color });

    const applePayScriptLoaded = () => {
        //@ts-ignore
        if (window.ApplePaySession) {
            var merchantIdentifier = 'merchant.io.checkplease.spreedly';
            //@ts-ignore
            var promise = ApplePaySession.applePayCapabilities(merchantIdentifier);
            promise.then(function (capabilities: any) {
                switch (capabilities.paymentCredentialStatus) {
                    case "paymentCredentialsAvailable":
                    case "paymentCredentialStatusUnknown":
                    case "paymentCredentialsUnavailable":
                            const applePayDiv: HTMLDivElement = applePayEl.current!!
                            applePayDiv.innerHTML = "<apple-pay-button onclick='var data = {action: \"applePay\"}; window.postMessage(data);' id='applePayButton' buttonstyle='black' type='check-out' style='margin-top: 20px !important; margin-bottom: 0px !important; --apple-pay-button-width: 300px; --apple-pay-button-height: 40px; --apple-pay-button-border-radius: 5px;--apple-pay-button-padding: 5px 0px;'></apple-pay-button>"
                            setApplePayLoaded(true);
                        break;
                }
        }); }
    }

    const applePayScriptError = () => {
        
    }

    const getApplePayScriptUrl = (): string => {
        return "https://applepay.cdn-apple.com/jsapi/v1/apple-pay-sdk.js"
    }

    const onApplePayClickHandler = async () => {
        var request = {
            countryCode: checkOutState.state.restaurantCountry,
            currencyCode: checkOutState.state.restaurantCurrency,
            supportedNetworks: getPaymentMethodArrayApple(),
            merchantCapabilities: ["supportsCredit", "supportsDebit","supports3DS"],
            total: { label: checkOutState.state.restaurantName!!, amount: (total / 100).toString() },
          }
          //@ts-ignore
          var session = new ApplePaySession(1, request);

          session.onvalidatemerchant = function(event: any) {
            const cancelToken = axios.CancelToken;
            const source = cancelToken.source();

            const applePaySessionRequest: ApplePaySessionRequest = {
                user_id: userState.state.userId!!,
                validation_url: event.validationUrl,
            }

            let url: string | undefined = process.env.REACT_APP_CHECKOUT_API_URL + "/location/" + checkOutState.state.restaurantId + "/checks/" + checkState.state.checkSummary!!.id + "/payment/applepay"

            const headers = {
                'X-API-Key': checkOutState.state.restaurantApiKey,
                'location_id': checkOutState.state.restaurantId
            }

            axios.post(url, applePaySessionRequest, { headers, cancelToken: source.token })
            .then(function(response) {
                let r = response.data as ApplePaySessionResponse;

                if (r.success === true) {
                    session.completeMerchantValidation(JSON.parse(r.session));
                }
                else {
                    setErrorMessage(String_System_GeneralError.find(s => s.key === userState.state.locale)!!.value);
                    setErrorDialogTryAgain(true);
                    setErrorDialogOpen(true);
                }
            })
            .catch(function(error) {
                if (error.response) {
                    setErrorMessage(String_System_GeneralError.find(s => s.key === userState.state.locale)!!.value);
                    setErrorDialogTryAgain(true);
                    setErrorDialogOpen(true);
                }
                else {
                    setErrorMessage(String_System_ConnectionError.find(s => s.key === userState.state.locale)!!.value);
                    setErrorDialogTryAgain(true);
                    setErrorDialogOpen(true);
                }
            });
        }

        session.onpaymentauthorized = function(event: any) {
            const cancelToken = axios.CancelToken;
            const source = cancelToken.source();

            console.log(JSON.stringify(event.payment.token.paymentData));

            const paymentDetail: PaymentDetail = {
                tip: checkState.state.tip,
                tax: checkState.state.checkTotal!!.tax,
                sub_total: checkState.state.checkTotal!!.sub_total,
                amount_to_pay: checkState.state.checkTotal!!.amount_due,
                total: checkState.state.checkTotal!!.total,
                zip_code: '',
                token: JSON.stringify(event.payment.token.paymentData),
                serial_number: '',
                extended_card_data: '',
                card_number_last_four: '',
                guest_last_name: '',
                room_number: '',
                session_id: ''
            }
    
            const request: MakePaymentRequest = {
                user_id: userState.state.userId!!,
                table_id: checkOutState.state.tableId,
                check_please_payment_id: checkState.state.paymentId!!,
                payment_method: null,
                payment_digital_wallet_type: PaymentDigitalWalletType.ApplePay,
                payment_detail: paymentDetail,
                globalpay_hpp_response: null,
                apple_pay_token: null,
                google_pay_token: null
            };

            const formData = new FormData();    
            formData.append('payment', JSON.stringify(request));

            let url = '/payment/process';

            axios.post(process.env.REACT_APP_CHECKOUT_API_URL + url, formData, { cancelToken: source.token })
            .then(function(response) {
                let r = response.data as MakePaymentResponse;
                
                if (r.success === true) {
                    session.completePayment((window as any).ApplePaySession.STATUS_SUCCESS);

                    const state: CheckState = checkState.state;
                    state.checkSummary = null;
                    state.checkDetail = null;
                    state.checkTotal = null;
                    state.paymentId = null;
                    state.splitType = SplitType.None;
                    state.splitPercentage = 0;
                    state.splitAmount = 0;
                    state.splitEvenlyCount = 0;
                    state.checkTotalWithTip = '';
                    state.tip = 0;
                    state.tipString = '';
                    checkState.setState(state);

                    if (r.restaurant_id != null) {
                        const coState: CheckOutState = checkOutState.state;
                        coState.restaurantId = r.restaurant_id;
                        checkOutState.setState(coState);
                    }
        
                    history.push({
                        pathname: '/postcheckout/review',
                        state: { 
                            paymentId: r.check_please_payment_id,
                        }
                    });
                }
                else {
                    session.completePayment((window as any).ApplePaySession.STATUS_FAILURE);

                    if (r.error_code === ErrorCode.CheckAlreadyPaid) {
                        setCheckPaidError(true);
                        setErrorDialogTryAgain(false);
                        setErrorMessage(String_Check_CheckPaid.find(s => s.key === userState.state.locale)!!.value);
                        setErrorDialogOpen(true);
                    }            
                    else if (r.error_code === ErrorCode.PaymentFailed)
                    {
                        setErrorMessage(String_PayCheck_ErrorPaymentFailed.find(s => s.key === userState.state.locale)!!.value);
                        setErrorDialogTryAgain(false);
                        setErrorDialogOpen(true);
                    }
                    else if (r.error_code === ErrorCode.PaymentAmountExceedsAmountDue)
                    {
                        setPaymentExceedsAmountDueError(true);
                        setErrorMessage(String_PayCheck_PaymentExceedsAmountDue.find(s => s.key === userState.state.locale)!!.value);
                        setErrorDialogTryAgain(false);
                        setErrorDialogOpen(true);
                    }
                }              
            })
            .catch(function(error) {
                session.completePayment((window as any).ApplePaySession.STATUS_FAILURE);
                
                if (error.response) {
                    setErrorMessage(String_System_GeneralError.find(s => s.key === userState.state.locale)!!.value);
                    setErrorDialogTryAgain(false);
                    setErrorDialogOpen(true);
                }
                else {
                    setErrorMessage(String_System_ConnectionError.find(s => s.key === userState.state.locale)!!.value);
                    setErrorDialogTryAgain(false);
                    setErrorDialogOpen(true);
                }
            });
        }

        session.oncancel = function (event: any) {
            
        }

        session.begin();
    }

    const messageHandler = (event: { data: any; }) => {
        try{
            if (event.data.action === "applePay") {
                onApplePayClickHandler();
            }
        }
        catch (exception) {

        }
    }

    const checkCanSubmit = () => {
        if (ccNumberReady && cvvReady && expMonth !== 'MM' && expYear !== 'YYYY') {
            readyHandler(true)
        }
        else {
            readyHandler(false)
        }
    }

    const scriptLoaded = () => {
        setIsLoaded(true)

        //@ts-ignore
        Spreedly.init(checkOutState.state.spreedlyEnvKey, {
            "numberEl": "spreedly-number",
            "cvvEl": "spreedly-cvv"
        });

        //@ts-ignore
        Spreedly.on('errors', function(errors) {
            errorHandler()

            var errorString = "Please fix the following errors: ";
            for (var i=0; i < errors.length; i++) {
              var error = errors[i];
              errorString += error.message + ", "
            };

            errorString = errorString.substring(0, errorString.length - 2);
            setIsError(false);
            setErrorMessage(errorString);
            setErrorDialogOpen(true);
          });

        //@ts-ignore
        Spreedly.on("ready", function () {
            //@ts-ignore
            Spreedly.setFieldType("cvv", "text");
            //@ts-ignore
            Spreedly.setFieldType("number", "text");
            //@ts-ignore
            Spreedly.setNumberFormat("prettyFormat");
            //@ts-ignore
            Spreedly.setStyle("number", "width:90%; height:36px; border: 1px solid #bfbfbf; border-radius: 4px; font-size: 1.4em;");
            //@ts-ignore
            Spreedly.setStyle("cvv", "width:80px; height:36px; border: 1px solid #bfbfbf; border-radius: 4px; font-size: 1.4em;");
        });

        //@ts-ignore
        Spreedly.on('paymentMethod', function(token, pmData) {
            successHandler(token);
        });

        //@ts-ignore
        Spreedly.on('fieldEvent', function(name, type, activeEl, inputProperties) {
            if(name === "number" || name === "cvv") {
                if (type === "input") { 
                    if (inputProperties["validNumber"]) {
                        setCcNumberReady(true)
                    }
                    else {
                        setCcNumberReady(false)
                    }

                    var validCvv = false;
                    if (inputProperties["validCvv"]) {
                        setCvvReady(true)
                    }
                    else {
                        setCvvReady(false)
                    }
                }              
            }            
          });
    }

    const scriptError = () => {

    }

    const onCreditButtonClick = () => {
        setIsCreditCardSelected(true);
    }

    const getPaymentMethodArrayGoogle = (): google.payments.api.CardNetwork[] => {
        const paymentMethodArray: google.payments.api.CardNetwork[] = []
        if (paymentMethodAllowed !== undefined) {
            let hasVisa = paymentMethodAllowed & PaymentMethodsAccepted.Visa;
            let hasMastercard = paymentMethodAllowed & PaymentMethodsAccepted.Mastercard;
            let hasAmex = paymentMethodAllowed & PaymentMethodsAccepted.Amex;
            let hasDiscover = paymentMethodAllowed & PaymentMethodsAccepted.Discover;
            if (hasVisa) {
                paymentMethodArray.push("VISA");
            }

            if (hasMastercard) {
                paymentMethodArray.push("MASTERCARD");
            }

            if (hasAmex) {
                paymentMethodArray.push("AMEX");
            }

            if (hasDiscover) {
                paymentMethodArray.push("DISCOVER");
            }
        }
        else {
            paymentMethodArray.push("VISA");
            paymentMethodArray.push("MASTERCARD");
            paymentMethodArray.push("AMEX");
            paymentMethodArray.push("DISCOVER");
        }

        return paymentMethodArray;
    }

    const getPaymentMethodArrayApple = (): string[] => {
        const paymentMethodArray: string[] = []

        if (paymentMethodAllowed !== undefined) {
            let hasVisa = paymentMethodAllowed & PaymentMethodsAccepted.Visa;
            let hasMastercard = paymentMethodAllowed & PaymentMethodsAccepted.Mastercard;
            let hasAmex = paymentMethodAllowed & PaymentMethodsAccepted.Amex;
            let hasDiscover = paymentMethodAllowed & PaymentMethodsAccepted.Discover;

            if (hasVisa) {
                paymentMethodArray.push("visa");
            }

            if (hasMastercard) {
                paymentMethodArray.push("masterCard");
            }

            if (hasAmex) {
                paymentMethodArray.push("amex");
            }

            if (hasDiscover) {
                paymentMethodArray.push("discover");
            }
        }
        else {
            paymentMethodArray.push("visa");
            paymentMethodArray.push("masterCard");
            paymentMethodArray.push("amex");
            paymentMethodArray.push("discover");
        }   

        return paymentMethodArray;
    }

    React.useEffect(() => {
        if (submitPayment) {
            submitPaymentForm()
        }
        else {
            if (!isLoaded) {
                const LoadApplePayExternalScript = () => {
                    const externalScript = document.createElement("script");
                    externalScript.onload = applePayScriptLoaded;
                    externalScript.onerror = applePayScriptError;
                    externalScript.id = "external";
                    externalScript.async = true;
                    externalScript.type = "text/javascript";
                    document.body.appendChild(externalScript);
                    externalScript.src = getApplePayScriptUrl()
                }
    
                LoadApplePayExternalScript()

                if (isCreditCardSelected) {
                    const LoadExternalScript = () => {
                        const externalScript = document.createElement("script");
                        externalScript.onload = scriptLoaded;
                        externalScript.onerror = scriptError;
                        externalScript.id = "external";
                        externalScript.async = true;
                        externalScript.type = "text/javascript";
                        document.body.appendChild(externalScript);
                        externalScript.src = `https://core.spreedly.com/iframe/iframe-v1.min.js`;
                    }
    
                    LoadExternalScript()
                }
            }    

            checkCanSubmit()
        }

        window.removeEventListener("message", messageHandler);
        window.addEventListener("message", messageHandler);
                
    }, [ccNumberReady, cvvReady, expMonth, expYear, submitPayment, isCreditCardSelected]);

    const handleExpMonthChange = (event: React.ChangeEvent<{ value: unknown }>) => {
        setExpMonth(event.target.value as string)
        setIsError(false)
    };

    const handleExpYearChange = (event: React.ChangeEvent<{ value: unknown }>) => {
        setExpYear(event.target.value as string)
        setIsError(false)
    };

    const submitPaymentForm = () => {

        //@ts-ignore
        Spreedly.setParam('allow_blank_name', true);

        var requiredFields: any = {};
        requiredFields["month"] = expMonth;
        requiredFields["year"] = expYear;
      
        //@ts-ignore
        Spreedly.tokenizeCreditCard(requiredFields);
      }

    const handleErrorDialogClose = (event: React.MouseEvent<HTMLElement>) => {
        setErrorDialogOpen(false);
    }

    const errorDialogProps: ErrorDialogProps = {
        open: errorDialogOpen,
        message: errorMessage,
        tryAgain: false,
        onCloseHandler: handleErrorDialogClose,
    }

    const getMonthKeyValues = (): { key: string, value: string }[] => {
        let kvps: { key: string, value: string }[] = [];
    
        kvps.push({ key: "-1", value: "MM" })

        for (var i = 1; i <= 12; i++) {
            if (i < 10) {
                kvps.push({ key: i.toString(), value: "0" + i.toString()});
            }
            else {
                kvps.push({ key: i.toString(), value: i.toString()});
            }
        }
        
        return kvps;
    }

    const getYearKeyValues = (): { key: string, value: string }[] => {
        let kvps: { key: string, value: string }[] = [];

        kvps.push({ key: "-1", value: "YYYY" })

        let date =  parseInt(new Date().getFullYear().toString());

        for (var i = 0; i <= 20; i++) {
            kvps.push({ key: (i + date).toString(), value: (i + date).toString()});
        }
        
        return kvps;
    }

    const toggleMaskingButtonClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        //@ts-ignore
        Spreedly.toggleMask()
        setIsMasked(!isMasked)
    }

    return (
        <div className={clsx({
            [classes.root]: isPortrait,
            [classes.rootLandscape]: !isPortrait,
        })}>

            {isLoaded && 
                <div className={classes.paymentTerms}>
                    <PaymentTerms />
                </div>                
            }        

            {isLoaded && 
                <div className={classes.fieldContainer}>
                    
                    <Typography variant="body1" className={classes.fieldLabel}>
                        Card Number
                    </Typography>
                    <div className={classes.spreedlyNumberContainer}>
                        <div id="spreedly-number" className={classes.spreedlyFields}>

                        </div>
                        <div className={classes.spreedlyMaskedButton}>
                            {isMasked &&
                                <IconButton
                                    className={classes.maskButton}
                                    size="medium"
                                    onClick={toggleMaskingButtonClick}>
                                    <VisibilityOffIcon />
                                </IconButton>
                            }
                            {!isMasked &&
                                <IconButton
                                    className={classes.maskButton}
                                    size="medium"
                                    onClick={toggleMaskingButtonClick}>
                                    <VisibilityIcon />
                                </IconButton>
                            }   
                             
                                                
                        </div>
                    </div>
                    <div className={classes.expDateCvvContainer}>
                        <div>
                            <Typography variant="body1" className={classes.fieldLabel}>
                                Expiration Date
                            </Typography>
                            <div className={classes.expFields}>
                                <form>
                                    <div className={classes.expFieldsInner}>
                                        <FormControl variant="outlined">
                                            <Select
                                                native
                                                className={classes.expMonth}
                                                id="cc-exp-month" 
                                                name="cc-exp-month" 
                                                autoComplete="cc-exp-month" 
                                                x-autocompletetype="cc-exp-month"
                                                value={expMonth}
                                                onChange={handleExpMonthChange}>
                                                {
                                                    getMonthKeyValues().map((kvp, index) => (
                                                        <option value={kvp.key} key={kvp.key}>{kvp.value}</option>
                                                    ))
                                                }
                                            </Select>
                                        </FormControl>

                                        <Typography variant="h5" className={classes.expDateDivider}>
                                            / 
                                        </Typography>

                                        <FormControl variant="outlined">
                                            <Select
                                                native
                                                className={classes.expYear}
                                                id="cc-exp-year" 
                                                name="cc-exp-year" 
                                                autoComplete="cc-exp-year" 
                                                x-autocompletetype="cc-exp-year"
                                                value={expYear}
                                                onChange={handleExpYearChange}>
                                                {
                                                    getYearKeyValues().map((kvp, index) => (
                                                        <option value={kvp.key} key={kvp.key}>{kvp.value}</option>
                                                    ))
                                                }
                                            </Select>
                                        </FormControl>
                                    </div>
                                </form>
                            </div>
                        </div>
                        <div>
                            <Typography variant="body1" className={classes.fieldLabel}>
                                CVV
                            </Typography>
                            <div id="spreedly-cvv" className={classes.spreedlyFields}>

                            </div>
                        </div>
                    </div>
                    <div className={classes.errorMessageContainer}>
                        {isError &&
                            <Typography variant='body1' className={classes.errorMessage}>
                                {String_PayCheck_CardExpirationDateFormatError.find(s => s.key === userState.state.locale)!!.value}
                            </Typography>
                        }
                    </div>
                </div>
            }

            {!isCreditCardSelected &&
                <div className={classes.creditButton} id="btnCredit" onClick={onCreditButtonClick}>Credit Card</div>
            }    

            {!isCreditCardSelected && ((checkOutState.state.restaurantGooglePayMerchantId !== null) || (checkOutState.state.restaurantGooglePayMerchantId !== null)) &&
                <div>
                    {(checkOutState.state.applePayMerchantId !== null) &&
                        <div ref={applePayEl}>

                        </div> 
                    }
                    {(checkOutState.state.restaurantGooglePayMerchantId !== null) && 
                        <GooglePayButton
                            className={classes.googlePayButton}
                            environment={process.env.REACT_APP_GOOGLEPAY_ENVIRONMENT!! as google.payments.api.Environment}
                            buttonColor="default"
                            buttonType="checkout"
                            buttonSizeMode="fill"
                            onLoadPaymentData={paymentRequest => {
                                googlePaySuccessHandler(paymentRequest)
                            }}
                            onError={error => {
                                console.log(error);
                                setErrorMessage(String_System_GeneralError.find(s => s.key === userState.state.locale)!!.value);
                                setErrorDialogTryAgain(true);
                                setErrorDialogOpen(true);
                            }}
                            paymentRequest={{
                                apiVersion: 2,
                                apiVersionMinor: 0,
                                allowedPaymentMethods: [
                                {
                                    type: 'CARD',
                                    parameters: {
                                    allowedAuthMethods: ['PAN_ONLY', 'CRYPTOGRAM_3DS'],
                                    allowedCardNetworks: getPaymentMethodArrayGoogle(),
                                    },
                                    tokenizationSpecification: {
                                    type: 'PAYMENT_GATEWAY',
                                    parameters: {
                                        gateway: 'spreedly',
                                        gatewayMerchantId: checkOutState.state.spreedlyEnvKey!!,
                                    },
                                    },
                                },
                                ],
                                merchantInfo: {
                                    merchantId: checkOutState.state.restaurantGooglePayMerchantId!!,
                                    merchantName: checkOutState.state.restaurantGooglePayMerchantName!!,
                                },
                                transactionInfo: {
                                totalPriceStatus: 'FINAL',
                                totalPriceLabel: 'Total',
                                totalPrice: (total / 100).toString(),
                                currencyCode: checkOutState.state.restaurantCurrency!!,
                                countryCode: checkOutState.state.restaurantCountry!!,
                                },
                            }}
                        />
                    }
                </div>
            }

            <ErrorDialog
                open={errorDialogProps.open}
                message={errorDialogProps.message}
                tryAgain={errorDialogProps.tryAgain}
                onCloseHandler={errorDialogProps.onCloseHandler} />
        </div>
        
    );
}